"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import { usePullToClose } from '@/hooks/usePullToClose';
import { useScrollLock } from '@/hooks/useScrollLock';
import { useClickOutsideHandler } from '@/hooks/useClickOutsideHandler';
import { MapPin, X, LocateFixed, Users, Package, Plane, Car, Bike, Truck, CarTaxiFront, Gift, Binoculars, Motorbike, Road } from '@/components/icons';
import { ChevronLeft, Bus, TramFront, PlaneLanding, PlaneTakeoff } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { useRequestRide, type LatLng } from '@/hooks/useRequestRide';
import { useSession } from '@/context/SessionContext';
import { useRequestRideContext } from '@/context/RequestRideContext';
import { useCheckout } from '@/context/CheckoutContext';
import { useOrderSidebar } from '@/context/OrderSidebarContext';
import { useDriversAvailability } from '@/context/DriversAvailabilityContext';
import { useSidebarWidth } from '@/hooks/useSidebarWidth';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { formatCurrency, cn, formatAddressForDisplay } from '@/lib/utils';
import { getUserAddresses, type UserAddress } from '@/lib/supabase/userAddresses';
import { getAirportByCity, getBusTerminalByCity, getTrainStationByCity, findCityLandmarkByDisplayName } from '@/lib/defaultLocations';
import { motion, AnimatePresence } from 'framer-motion';
import { AddressPickerSidebar } from './AddressPickerSidebar';
import LocationAutocomplete from './LocationAutocomplete';
import { ToursCardList, type Tour } from './tours/ToursCardList';
import { TourCitySelector, type TourCity } from './tours/TourCitySelector';
import { TourInfoSidebar } from './tours/TourInfoSidebar';
import { PassengerDetailsSidebar, type Passenger } from './tours/PassengerDetailsSidebar';
import { PaymentSidebar } from './PaymentSidebar';
import { TourCheckoutSummary } from './tours/TourCheckoutSummary';
import { addCartItem, clearCartItemsForStore, updateCartNotes, getOpenCartsForUser, removeCartItem, updateCartItemsDetailsForStore } from '@/lib/supabase/carts';
import { getOrdersByUserId, updateOrderStatus } from '@/lib/supabase/orders';
import { getDispatchStatusLabel, getDispatchStatusProgress, getStoreStatusLabel, getStoreStatusProgress } from '@/lib/orderStatusLabels';
import { getProductById } from '@/lib/supabase/products';
import { addToTempCart, getTempCartAsItems, removeFromTempCart } from '@/lib/tempCart';
import { useTourBooking, type TourReservationPayload } from '@/hooks/useTourBooking';
import type { NoteLocation } from '@/types/location';

interface AddressOption {
  id: string;
  displayName: string;
  coords: LatLng;
  floor?: string | null;
  apartment?: string | null;
  salutation?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  distance?: number;
}

type TransportOption = {
  value: 'light' | 'auto' | 'van';
  label: string;
  icons: React.ElementType[];
  fee: number | null;
  duration: number | null;
  disabled?: boolean;
  disabledReason?: string;
};

const isMinibidDeliveryOrder = (order: any): boolean => {
  if (!order) return false;

  const pickupAddress = String(order.delivery_pickup_address || order.pickup_address_name || '').trim();
  const deliveryAddress = String(order.delivery_address || order.delivery_address_name || '').trim();
  const requiredTransportType = String(order.required_transport_type || '').trim();
  const hasTransportType = requiredTransportType.length > 0;
  const hasDeliveryRoute = pickupAddress.length > 0 || deliveryAddress.length > 0;
  const fee = Number(order.delivery_fee);
  const hasDeliveryFee = Number.isFinite(fee) && fee > 0;

  return hasTransportType || hasDeliveryRoute || hasDeliveryFee;
};

const isOrderOpenForDeliveryCards = (order: any): boolean => {
  const storeStatus = String(order?.status || '').trim().toLowerCase();
  const logisticsStatus = String(order?.dispatch_status || '').trim().toLowerCase();

  if (storeStatus === 'completed' || storeStatus === 'fulfilled') return false;
  if (logisticsStatus === 'completed' || logisticsStatus === 'delivered') return false;

  return true;
};

const getDeliveryOrderTone = (status: string | null | undefined) => {
  if (status === 'fulfilled') {
    return {
      card: 'border-emerald-500/20',
      ring: 'group-hover:shadow-[0_0_0_1px_rgba(16,185,129,0.18),0_18px_36px_rgba(16,185,129,0.12)]',
    };
  }

  if (status === 'cancelled') {
    return {
      card: 'border-rose-500/20',
      ring: 'group-hover:shadow-[0_0_0_1px_rgba(244,63,94,0.18),0_18px_36px_rgba(244,63,94,0.12)]',
    };
  }

  return {
    card: 'border-primary/25',
    ring: 'group-hover:shadow-[0_0_0_1px_rgba(99,102,241,0.2),0_22px_44px_rgba(99,102,241,0.14)]',
  };
};

export function RequestRideSidebar() {
  const { isOpen, initialServiceType, entryPoint, close } = useRequestRideContext();
  const { openSidebar: openCheckoutSidebar } = useCheckout();
  const { openSidebar: openOrderSidebar } = useOrderSidebar();
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = React.useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      close();
      setIsClosing(false);
    }, 300);
  }, [close]);

  const { session, profile, relevantLocation } = useSession();
  const { availability: driversAvailability } = useDriversAvailability();
  const { maxWidth } = useSidebarWidth();
  const { isDesktop, bottomNavHeight, recalculate } = useHeaderOffset();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const addressPickerSidebarRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { loading, error, calculateFee, requestRide } = useRequestRide();
  const [feeError, setFeeError] = useState<string | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef<boolean>(false);
  const calculationSkeletonRef = useRef<HTMLDivElement>(null);
  const fareCalcRequestIdRef = useRef(0);

  useScrollLock(isOpen || isClosing, false, scrollAreaRef);

  // Form state
  const [transportType, setTransportType] = useState(() => (initialServiceType === 'paquete' ? 'light' : 'auto'));
  const [selectedTransportFee, setSelectedTransportFee] = useState<number | null>(null);
  // Always use 'paquete' (delivery) - service type selector removed from UX
  const serviceType = 'paquete';
  const [selectedPickup, setSelectedPickup] = useState<AddressOption | null>(null);
  const [selectedDelivery, setSelectedDelivery] = useState<AddressOption | null>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [fee, setFee] = useState<number | null>(null);
  const [currentPriceBase, setCurrentPriceBase] = useState<number>(350);
  const [currentPricePerKm, setCurrentPricePerKm] = useState<number>(120);
  const [calculating, setCalculating] = useState(false);
  const [transportOptions, setTransportOptions] = useState<TransportOption[]>([]);
  
  // Tours-specific state
  const [selectedTours, setSelectedTours] = useState<Tour[]>([]);
  const [tourQuantity, setTourQuantity] = useState(1);
  const [showPassengerSidebar, setShowPassengerSidebar] = useState(false);
  const [selectedCity, setSelectedCity] = useState<TourCity | null>(null);
  const [checkoutOrder, setCheckoutOrder] = useState<{ id: string; date: string; total: number } | null>(null);
  const [deliveryOrders, setDeliveryOrders] = useState<any[]>([]);
  const [loadingDeliveryOrders, setLoadingDeliveryOrders] = useState(false);
  const [visibleDeliveryOrdersCount, setVisibleDeliveryOrdersCount] = useState(1);

  // Tours checkout hook
  const { createTourReservation } = useTourBooking();

  // Track tour payment data
  const [tourPaymentData, setTourPaymentData] = useState<{ passengers: Passenger[]; tourDays: any[]; totalPrice: number } | null>(null);
  
  // Pre-loaded passenger data from cart notes (used as fallback in PassengerDetailsSidebar)
  const [cartPassengerData, setCartPassengerData] = useState<{ passengers: Passenger[]; tourDays: any[] } | null>(null);

  // Map product_id -> cart_item_id for tours currently in cart (enables removal on deselect)
  const [cartItemIdsByProductId, setCartItemIdsByProductId] = useState<Record<string, string>>({});

  const [pendingOrderCancel, setPendingOrderCancel] = useState<any | null>(null);

  const confirmPendingOrderCancel = useCallback(async () => {
    if (!pendingOrderCancel?.id) return;
    const orderToCancel = pendingOrderCancel;
    setPendingOrderCancel(null);

    try {
      await updateOrderStatus(orderToCancel.id, 'cancelled');
      toast({
        title: 'Pedido cancelado',
        description: `Se canceló el pedido #${orderToCancel.order_number || orderToCancel.id.slice(0, 8)} antes de convocar repartidor.`,
      });
      if (session?.user?.id) {
        const allOrders = await getOrdersByUserId(session.user.id);
        setDeliveryOrders((allOrders || []).filter((o: any) => isMinibidDeliveryOrder(o) && isOrderOpenForDeliveryCards(o)));
      }
    } catch (error) {
      console.error('[RequestRideSidebar] Error cancelling in-progress order:', error);
      toast({
        title: 'Error',
        description: `No pudimos cancelar el pedido. ${error instanceof Error ? error.message : String(error)}`,
        variant: 'destructive',
      });
    }
  }, [pendingOrderCancel, session?.user?.id, toast]);

  const renderDeliveryOrderCard = useCallback((order: any) => {
    const store = order?.stores;
    const statusTone = getDeliveryOrderTone(order?.status);
    const storeStatusText = getStoreStatusLabel(order?.status);
    const logisticsStatusText = getDispatchStatusLabel(order?.dispatch_status);
    const storeStatusProgress = Math.min(100, Math.max(0, getStoreStatusProgress(order?.status)));
    const logisticsStatusProgress = Math.min(100, Math.max(0, getDispatchStatusProgress(order?.dispatch_status)));
    const pickupAddress = String(order?.delivery_pickup_address || order?.pickup_address_name || '').trim();
    const destinationAddress = String(order?.delivery_address || order?.delivery_address_name || '').trim();
    const routeText = [pickupAddress, destinationAddress].filter(Boolean).join(' → ');
    const fetchedStoreCost = order?.subtotal ?? order?.products_subtotal ?? order?.total ?? null;
    const fetchedDeliveryCost = order?.delivery_fee ?? order?.delivery_total ?? order?.shipping_total ?? null;
    const normalizedStoreCost = fetchedStoreCost != null ? Number(fetchedStoreCost) : null;
    const normalizedDeliveryCost = fetchedDeliveryCost != null ? Number(fetchedDeliveryCost) : null;
    const hasValidStoreCost = normalizedStoreCost !== null && Number.isFinite(normalizedStoreCost);
    const hasValidDeliveryCost = normalizedDeliveryCost !== null && Number.isFinite(normalizedDeliveryCost);
    const fetchedOrderTotal = order?.total != null && Number.isFinite(Number(order.total))
      ? Number(order.total)
      : null;
    const totalPlusDelivery = fetchedOrderTotal !== null
      ? Number((fetchedOrderTotal + (normalizedDeliveryCost ?? 0)).toFixed(2))
      : null;
    const totalFromParts = hasValidStoreCost && hasValidDeliveryCost
      ? Number((normalizedStoreCost + normalizedDeliveryCost).toFixed(2))
      : null;

    const dispatchStatusKey = order?.dispatch_status || 'pending';
    const canCancelBeforeCourier = !['picked_up', 'delivered', 'completed', 'cancelled'].includes(dispatchStatusKey);

    return (
      <div
        key={order.id}
        role="button"
        tabIndex={0}
        onClick={() => {
          handleClose();
          setTimeout(() => {
            openOrderSidebar(order.id);
          }, 220);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          handleClose();
          setTimeout(() => {
            openOrderSidebar(order.id);
          }, 220);
        }}
        className={cn(
          'group relative w-full overflow-hidden rounded-2xl border bg-gradient-to-br from-card via-card to-primary/5 p-2.5 sm:p-3.5 text-left transition-all duration-200 ease-out outline-none cursor-pointer',
          'hover:-translate-y-0.5 hover:border-primary/35 focus-visible:-translate-y-0.5 focus-visible:border-primary/35',
          statusTone.card,
          statusTone.ring
        )}
      >
        <div className="pointer-events-none absolute -right-16 -top-16 h-28 w-28 rounded-full bg-primary/10 blur-2xl opacity-70" />
        <div className="relative z-10 flex items-start justify-between gap-3 min-w-0">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            {store ? (
              <Avatar className="h-11 w-11 self-start rounded-xl shrink-0 border border-primary/20 ring-1 ring-primary/10">
                {store.avatar_url ? (
                  <AvatarImage src={store.avatar_url} className="rounded-xl" />
                ) : (
                  <AvatarFallback className="rounded-xl bg-primary/15 text-primary font-semibold">
                    {(store.name || 'T')[0]}
                  </AvatarFallback>
                )}
              </Avatar>
            ) : (
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
                <Package className="h-5 w-5" />
              </div>
            )}
            <div className="min-w-0 max-w-full overflow-hidden">
              <div className="font-semibold text-sm truncate text-foreground">{store?.name || 'Tienda'}</div>
              <div className="mt-0.5 text-xs text-muted-foreground truncate" title={order.order_number || order.id || '—'}>
                #{order.order_number || order.id?.slice(0, 8) || '—'}
              </div>
              <span className="mt-1 inline-flex items-center text-[11px] text-muted-foreground whitespace-nowrap">
                {order?.created_at ? format(new Date(order.created_at), 'dd/MM/yyyy HH:mm') : '—'}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0 min-w-0 max-w-[12rem]">
            <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground/80 truncate">Total</p>
            <p className="text-base font-extrabold text-primary leading-tight truncate">
              {totalPlusDelivery != null ? formatCurrency(totalPlusDelivery) : (totalFromParts != null ? formatCurrency(totalFromParts) : '—')}
            </p>
          </div>
        </div>

        <div className="relative z-10 mt-3 rounded-lg border border-border/60 bg-background/70 p-2">
          <div className="grid grid-cols-1 gap-2">
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-medium text-muted-foreground">Tienda ({storeStatusText})</span>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                  {normalizedStoreCost != null ? formatCurrency(normalizedStoreCost) : '—'} · {storeStatusProgress}%
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-300"
                  style={{ width: `${storeStatusProgress}%` }}
                />
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-medium text-muted-foreground">Envío ({logisticsStatusText})</span>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                  {normalizedDeliveryCost != null ? formatCurrency(normalizedDeliveryCost) : '—'} · {logisticsStatusProgress}%
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                  style={{ width: `${logisticsStatusProgress}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {dispatchStatusKey === 'delivered' || dispatchStatusKey === 'completed' || dispatchStatusKey === 'cancelled' || order?.status === 'cancelled' ? null : (
          <div className="mt-2 space-y-2">
            {canCancelBeforeCourier ? (
              <AnimatePresence mode="wait" initial={false}>
                {pendingOrderCancel?.id === order.id ? (
                  <motion.div
                    key="confirm-cancel"
                    initial={{ opacity: 0, y: -4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.98 }}
                    transition={{ duration: 0.16, ease: 'easeOut' }}
                    className="space-y-2 rounded-xl border border-destructive/20 bg-destructive/5 p-2.5 sm:p-3"
                  >
                    <p className="text-xs font-medium text-destructive leading-tight font-sans">
                      Seguro que quieres cancelar este pedido. La tienda gestionará tu devolución.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setPendingOrderCancel(null);
                        }}
                        className="flex-1 rounded-full border border-border bg-background px-2 sm:px-3 py-1.5 sm:py-2 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted"
                      >
                        No Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          void confirmPendingOrderCancel();
                        }}
                        className="flex-1 rounded-full border border-destructive/25 bg-destructive/10 px-2 sm:px-3 py-1.5 sm:py-2 text-xs font-semibold text-destructive transition-colors hover:bg-destructive/15"
                      >
                        Cancelar
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.button
                    key="cancel-button"
                    type="button"
                    initial={{ opacity: 0, y: 4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 4, scale: 0.98 }}
                    transition={{ duration: 0.16, ease: 'easeOut' }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setPendingOrderCancel(order);
                    }}
                    className="inline-flex w-fit max-w-full items-center justify-start gap-1.5 rounded-full px-0 py-0.5 text-[11px] font-normal text-muted-foreground/70 transition-colors hover:text-muted-foreground"
                  >
                    <X className="h-3 w-3" />
                    Cancelar pedido
                  </motion.button>
                )}
              </AnimatePresence>
            ) : (
              <div className="space-y-2">
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-border bg-muted/60 px-3 py-2 text-xs font-semibold text-muted-foreground/80 opacity-75 cursor-not-allowed"
                >
                  <X className="h-3.5 w-3.5" />
                  Cancelar pedido
                </button>
                <p className="text-[11px] leading-snug text-muted-foreground">
                  Tu repartidor ya está yendo a entregarte el pedido. Recíbelo y gestiona la devolución con la tienda. Debes pagar {normalizedDeliveryCost != null ? formatCurrency(normalizedDeliveryCost) : 'el costo del envío'} al repartidor por su servicio canceles o no.
                </p>
              </div>
            )}
          </div>
        )}

        {routeText && (
          <div className="relative z-10 mt-2 flex items-start gap-1.5 pl-2 text-xs text-muted-foreground">
            <Road className="h-3.5 w-3.5 mt-[1px] shrink-0" />
            <span className="whitespace-normal break-words">{routeText}</span>
          </div>
        )}
      </div>
    );
  }, [handleClose, openOrderSidebar, pendingOrderCancel, confirmPendingOrderCancel]);

  // Auto-scroll a resultados cuando están listos
  useEffect(() => {
    if (calculating || !distance || !fee || !calculationSkeletonRef.current) return;
    if (hasScrolledRef.current) return; // Solo una vez

    hasScrolledRef.current = true;
    
    // Scroll suave al contenedor de resultados (con offset para dejar aire arriba)
    setTimeout(() => {
      const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
      if (!viewport || !calculationSkeletonRef.current) return;
      
      const rect = calculationSkeletonRef.current.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();
      const relativeTop = rect.top - viewportRect.top;
      const offset = relativeTop - 15; // Resta 15px para dejar aire arriba
      
      viewport.scrollTo({
        top: viewport.scrollTop + offset,
        behavior: 'smooth'
      });
    }, 50);
  }, [calculating, distance, fee]);
  
  // Tour info sidebar state
  const [showTourInfo, setShowTourInfo] = useState<Tour | null>(null);
  
  // Address lists
  const [addresses, setAddresses] = useState<UserAddress[]>([]);

  // Address Picker Sidebar state
  const [addressPickerOpen, setAddressPickerOpen] = useState<'pickup' | 'delivery' | null>(null);
  const [initialLocationForPicker, setInitialLocationForPicker] = useState<{ lat: number; lng: number } | null>(null);
  const [initialDisplayNameForPicker, setInitialDisplayNameForPicker] = useState<string | null>(null);
  const [forceAlturaZeroForPicker, setForceAlturaZeroForPicker] = useState(false);

  const normalizeLatLng = useCallback((lat: unknown, lng: unknown): { lat: number; lng: number } | null => {
    const parsedLat = typeof lat === 'number' ? lat : Number(lat);
    const parsedLng = typeof lng === 'number' ? lng : Number(lng);
    if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLng)) return null;
    return { lat: parsedLat, lng: parsedLng };
  }, []);

  const syncToursFromCart = useCallback(async () => {
    const effectiveServiceType = initialServiceType || serviceType;
    if (effectiveServiceType !== 'tours') return;

    if (session?.user?.id) {
      const carts = await getOpenCartsForUser(session.user.id);
      const tourItems = (carts || []).flatMap((cart: any) =>
        (cart.cart_items || []).filter((item: any) => item?.products?.product_type === 'tours')
      );

      if (tourItems.length === 0) {
        setSelectedTours([]);
        setCartItemIdsByProductId({});
        setCartPassengerData(null);
        return;
      }

      const tourProducts = await Promise.all(
        tourItems.map((item: any) => getProductById(item.product_id))
      );

      const tours: Tour[] = tourProducts
        .filter((p): p is NonNullable<typeof p> => p !== null)
        .map((p) => ({
          id: p.id,
          title: p.title,
          description: (p as any).description || '',
          fixed_sale_price: Number((p as any).fixed_sale_price ?? 0),
          latitude: Number((p as any).latitude ?? 0),
          longitude: Number((p as any).longitude ?? 0),
          departure_time: (p as any).departure_time ?? undefined,
          timezone: (p as any).timezone ?? undefined,
          main_image_url: (p as any).main_image_url ?? undefined,
          image_urls: (p as any).image_urls ?? undefined,
          details: (p as any).details ?? undefined,
          store_id: p.store_id ?? undefined,
          store_name: (p as any).store_name ?? undefined,
          is_digital: (p as any).is_digital ?? undefined,
        }));

      setSelectedTours(tours);
      const maxQty = Math.max(...tourItems.map((item: any) => Number(item.quantity || 1)));
      setTourQuantity(maxQty);

      const idMap: Record<string, string> = {};
      tourItems.forEach((item: any) => {
        if (item.product_id && item.id) idMap[item.product_id] = item.id;
      });
      setCartItemIdsByProductId(idMap);

      const cartWithNotes = (carts || []).find((cart: any) => {
        try {
          const parsed = JSON.parse(cart.notes || '');
          return parsed?.type === 'tour_bundle' || parsed?.type === 'tour_cart_passenger_details';
        } catch {
          return false;
        }
      });

      if (!cartWithNotes) {
        setCartPassengerData(null);
        return;
      }

      try {
        const notesData = JSON.parse(cartWithNotes.notes);
        const passengers: Passenger[] = Array.isArray(notesData?.passengers)
          ? notesData.passengers.map((p: any) => ({
              id: p.id || crypto.randomUUID(),
              name: p.name || '',
              documentNumber: p.documentNumber || '',
              nationality: p.nationality || '',
            }))
          : [];
        const tourDays = Array.isArray(notesData?.itinerary)
          ? notesData.itinerary.map((d: any) => ({
              date: d.date,
              tour: tours.find((t) => t.id === d.tourId) || { id: d.tourId, title: d.tourTitle },
              destination: d.destination ? { displayName: d.destination, coords: { lat: 0, lng: 0 } } : undefined,
            }))
          : [];

        if (passengers.length > 0) {
          setCartPassengerData({ passengers, tourDays });
        } else {
          setCartPassengerData(null);
        }
      } catch {
        setCartPassengerData(null);
      }

      return;
    }

    const tempItems = getTempCartAsItems();
    if (tempItems.length === 0) {
      setSelectedTours([]);
      setCartItemIdsByProductId({});
      return;
    }

    const tempProducts = await Promise.all(
      tempItems.map((item: any) => getProductById(item.product_id))
    );

    const tempTourEntries = tempItems
      .map((item: any, index: number) => ({ item, product: tempProducts[index] }))
      .filter(({ product }) => product && (product as any).product_type === 'tours');

    const tours: Tour[] = tempTourEntries.map(({ product }) => ({
      id: product!.id,
      title: product!.title,
      description: (product as any).description || '',
      fixed_sale_price: Number((product as any).fixed_sale_price ?? 0),
      latitude: Number((product as any).latitude ?? 0),
      longitude: Number((product as any).longitude ?? 0),
      departure_time: (product as any).departure_time ?? undefined,
      timezone: (product as any).timezone ?? undefined,
      main_image_url: (product as any).main_image_url ?? undefined,
      image_urls: (product as any).image_urls ?? undefined,
      details: (product as any).details ?? undefined,
      store_id: product!.store_id ?? undefined,
      store_name: (product as any).store_name ?? undefined,
      is_digital: (product as any).is_digital ?? undefined,
    }));

    setSelectedTours(tours);
    const maxQty = Math.max(...tempTourEntries.map(({ item }) => Number(item.quantity || 1)));
    setTourQuantity(maxQty);
    const idMap: Record<string, string> = {};
    tempTourEntries.forEach(({ item }) => {
      if (item.product_id && item.id) idMap[item.product_id] = item.id;
    });
    setCartItemIdsByProductId(idMap);
  }, [initialServiceType, serviceType, session?.user?.id]);

  const toggleTourSelection = useCallback(async (tour: Tour) => {
    const exists = selectedTours.some((selectedTour) => selectedTour.id === tour.id);

    try {
      if (exists) {
        const cartItemId = cartItemIdsByProductId[tour.id];
        if (session?.user?.id) {
          if (cartItemId) {
            await removeCartItem(cartItemId);
          }
        } else {
          removeFromTempCart(tour.id);
        }
      } else if (session?.user?.id) {
        await addCartItem(session.user.id, tour.id, 1);
      } else {
        addToTempCart({
          id: tour.id,
          title: tour.title,
          fixed_sale_price: tour.fixed_sale_price,
        }, 1);
      }

      await syncToursFromCart();
      window.dispatchEvent(new CustomEvent('cart-updated'));
    } catch (error) {
      console.error('[RequestRideSidebar] Error updating tour cart state:', error);
      toast({
        title: 'Error',
        description: 'No pudimos actualizar esta excursión en tu canasto',
        variant: 'destructive',
      });
    } finally {
    }
  }, [cartItemIdsByProductId, selectedTours, session?.user?.id, syncToursFromCart, toast]);

  const handleOpenToursCheckout = useCallback(() => {
    handleClose();
    setTimeout(() => {
      openCheckoutSidebar();
    }, 320);
  }, [handleClose, openCheckoutSidebar]);

  const handleClearSelectedTours = useCallback(async () => {
    const tourIds = selectedTours.map((tour) => tour.id);
    if (tourIds.length === 0) return;

    try {
      if (session?.user?.id) {
        await Promise.all(
          tourIds.map((tourId) => {
            const cartItemId = cartItemIdsByProductId[tourId];
            return cartItemId ? removeCartItem(cartItemId) : Promise.resolve();
          })
        );
      } else {
        tourIds.forEach((tourId) => removeFromTempCart(tourId));
      }

      await syncToursFromCart();
      window.dispatchEvent(new CustomEvent('cart-updated'));
    } catch (error) {
      console.error('[RequestRideSidebar] Error clearing selected tours:', error);
      toast({
        title: 'Error',
        description: 'No pudimos limpiar las excursiones del canasto',
        variant: 'destructive',
      });
    }
  }, [cartItemIdsByProductId, selectedTours, session?.user?.id, syncToursFromCart, toast]);

  // Click fuera para cerrar
  useClickOutsideHandler({
    isOpen,
    sidebarRef,
    handleClose,
    ignoreRefs: [addressPickerSidebarRef],
  });

  // Cerrar con tecla ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);

  // Recalculate header offset when opening sidebar to ensure correct height on Safari
  useEffect(() => {
    if (isOpen) {
      recalculate();
      const timeoutId = setTimeout(recalculate, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, recalculate]);

  // Activar pull-to-close (solo en móvil)
  usePullToClose(scrollAreaRef, {
    onClose: handleClose,
    threshold: 80,
    enabled: isOpen && !isDesktop
  });

  const getEffectiveFeeForTransport = useCallback((type: string): number | null => {
    const optionFee = transportOptions.find((option) => option.value === type)?.fee;
    const fallbackFee = type === transportType ? fee : null;
    const baseFee = optionFee ?? fallbackFee;
    if (baseFee === null || baseFee === undefined) return null;
    return Math.round(type === 'light' ? baseFee * 0.7 : baseFee);
  }, [transportOptions, transportType, fee]);

  const selectedTransportIcons = React.useMemo(() => {
    if (transportType === 'light') return [Bike];
    if (transportType === 'van') return [Truck];
    return [Car];
  }, [transportType]);

  const selectedTransportLabel = React.useMemo(() => {
    if (transportType === 'light') return 'Bici';
    if (transportType === 'van') return 'Camioneta';
    return 'Coche';
  }, [transportType]);

  // Initialize transportType on mount (always light for paquete)
  useEffect(() => {
    setTransportType('light');
  }, []);

  useEffect(() => {
    setSelectedTransportFee(getEffectiveFeeForTransport(transportType));
  }, [transportType, transportOptions, fee, getEffectiveFeeForTransport]);

  useEffect(() => {
    const optionDuration = transportOptions.find((option) => option.value === transportType)?.duration;
    if (optionDuration !== null && optionDuration !== undefined) {
      setDuration(optionDuration);
    }
  }, [transportType, transportOptions]);

  // Reset scroll flag cuando cambia transportType
  useEffect(() => {
    hasScrolledRef.current = false;
  }, [transportType]);

  // Sync tours already in cart when opening the sidebar in tours mode
  useEffect(() => {
    let cancelled = false;
    if (!isOpen) return;

    void syncToursFromCart().catch((e) => {
      if (!cancelled) {
        console.error('[RequestRideSidebar] Error syncing cart tours:', e);
      }
    });

    return () => { cancelled = true; };
  }, [isOpen, syncToursFromCart]);

  // Reset autoscroll flag cuando se abre/cierra el sidebar
  useEffect(() => {
    if (!isOpen) {
      hasScrolledRef.current = false;
    }
  }, [isOpen]);

  // Cargar direcciones del usuario (o de localStorage para no logueados)
  useEffect(() => {
    if (!isOpen) return;
    
    if (session?.user?.id) {
      // Usuario logueado: cargar de Supabase
      getUserAddresses(session.user.id)
        .then(setAddresses)
        .catch(() => setAddresses([]));
    } else {
      // Usuario no logueado: cargar de localStorage
      try {
        const localAddresses = localStorage.getItem('pending_addresses');
        if (localAddresses) {
          const parsed = JSON.parse(localAddresses);
          // Convertir formato de localStorage a UserAddress
          const convertedAddresses = parsed.map((addr: any) => ({
            id: addr.id,
            user_id: null,
            label: addr.label,
            is_primary: addr.is_primary || false,
            location: addr.location,
            floor: addr.floor,
            apartment: addr.apartment,
            notes: addr.notes,
            altura: addr.altura,
            streetName: addr.streetName,
            created_at: addr.created_at,
            updated_at: addr.updated_at,
          }));
          setAddresses(convertedAddresses);
        } else {
          setAddresses([]);
        }
      } catch (error) {
        console.error('[RequestRideSidebar] Error loading local addresses:', error);
        setAddresses([]);
      }
    }
  }, [isOpen, session?.user?.id]);

  useEffect(() => {
    if (!isOpen || serviceType === 'tours') return;
    if (!session?.user?.id) {
      setDeliveryOrders([]);
      return;
    }

    let cancelled = false;

    const loadDeliveryOrders = async () => {
      try {
        setLoadingDeliveryOrders(true);
        const allOrders = await getOrdersByUserId(session.user.id);
        if (cancelled) return;

        setDeliveryOrders((allOrders || []).filter((order: any) => isMinibidDeliveryOrder(order) && isOrderOpenForDeliveryCards(order)));
        setVisibleDeliveryOrdersCount(1);
      } catch (err) {
        if (!cancelled) {
          console.error('[RequestRideSidebar] Error loading Minibid delivery orders:', err);
          setDeliveryOrders([]);
          setVisibleDeliveryOrdersCount(1);
        }
      } finally {
        if (!cancelled) {
          setLoadingDeliveryOrders(false);
        }
      }
    };

    void loadDeliveryOrders();

    return () => {
      cancelled = true;
    };
  }, [isOpen, serviceType, session?.user?.id]);

  // Escuchar cambios en direcciones sin refrescar la página
  useEffect(() => {
    if (!session?.user?.id) return;
    
    const handleAddressUpdated = async () => {
      try {
        const updatedAddresses = await getUserAddresses(session.user.id);
        setAddresses(updatedAddresses);
        
        // Si aún no se seleccionó pickup y hay una dirección primaria, seleccionarla
        if (!selectedPickup && updatedAddresses.length > 0) {
          const primaryAddress = updatedAddresses.find(a => a.is_primary) || updatedAddresses[0];
          if (primaryAddress?.location) {
            setSelectedPickup({
              id: primaryAddress.id,
              displayName: primaryAddress.location.displayName,
              coords: {
                lat: primaryAddress.location.latitude,
                lng: primaryAddress.location.longitude,
              },
              floor: primaryAddress.floor,
              apartment: primaryAddress.apartment,
            });
          }
        }
      } catch (error) {
        console.error('[RequestRideSidebar] Error loading addresses:', error);
      }
    };
    
    window.addEventListener('address-updated', handleAddressUpdated);
    return () => {
      window.removeEventListener('address-updated', handleAddressUpdated);
    };
  }, [session?.user?.id, selectedPickup]);

  // Reset auto-fill flag only when sidebar closes
  useEffect(() => {
    if (!isOpen) {
      hasScrolledRef.current = false;
    }
  }, [isOpen]);

  // Update pickup location when tours city is selected or when relevantLocation changes while sidebar is open
  useEffect(() => {
    // Only auto-set if service is EXACTLY 'tours' AND sidebar is OPEN
    if (!isOpen || serviceType !== 'tours') return;

    if (selectedCity) {
      setSelectedPickup({
        id: `city-${selectedCity.city_name}`,
        displayName: selectedCity.display_name,
        coords: { lat: selectedCity.latitude, lng: selectedCity.longitude },
      });
    } else if (!selectedPickup || selectedPickup.id === 'current-location') {
      // Use current "vigente" location whenever it changes or sidebar is opened
      const locationToUse = relevantLocation || (profile?.relevant_location_display_name && profile?.relevant_latitude && profile?.relevant_longitude ? {
        displayName: profile.relevant_location_display_name,
        latitude: profile.relevant_latitude,
        longitude: profile.relevant_longitude
      } : null);

      if (locationToUse) {
        // Only update if it's actually different to avoid cycles
        if (locationToUse.displayName !== selectedPickup?.displayName) {
          setSelectedPickup({
            id: 'current-location',
            displayName: locationToUse.displayName,
            coords: { lat: locationToUse.latitude, lng: locationToUse.longitude },
            floor: (profile as any)?.relevant_location_floor || null,
            apartment: (profile as any)?.relevant_location_apartment || null,
          });
        }
      }
    }
  }, [serviceType, selectedCity, profile, relevantLocation, isOpen]);

  // Reset auto-fill flag ONLY when sidebar closes, or when changing SERVICE TYPE away from tours
  useEffect(() => {
    if (!isOpen || serviceType !== 'tours') {
      // We also ensure that if we are NOT in tours, we don't hold the 'current-location' id
      // if it was set by the tours auto-fill (optional, depends on if you want it to persist)
    }
  }, [isOpen, serviceType]);

  // Helper function to check if a transport type has available drivers
  const getDriverAvailability = useCallback((transportType: 'light' | 'auto' | 'van') => {
    const availability = {
      light: (driversAvailability.bike || 0) + (driversAvailability.motorcycle || 0),
      auto: driversAvailability.car || 0,
      van: driversAvailability.van || 0,
    };
    return availability[transportType];
  }, [driversAvailability]);

  // Calcular tarifa cuando cambian las coordenadas
  useEffect(() => {
    if (!selectedPickup || !selectedDelivery) {
      fareCalcRequestIdRef.current += 1;
      setDistance(null);
      setDuration(null);
      setFee(null);
      setTransportOptions([]);
      setFeeError(null);
      return;
    }

    const validPickup = normalizeLatLng(selectedPickup.coords.lat, selectedPickup.coords.lng);
    const validDelivery = normalizeLatLng(selectedDelivery.coords.lat, selectedDelivery.coords.lng);

    if (!validPickup || !validDelivery) {
      fareCalcRequestIdRef.current += 1;
      setDistance(null);
      setDuration(null);
      setFee(null);
      setTransportOptions([]);
      setFeeError('No se pudo calcular la tarifa. Revisa los puntos seleccionados.');
      return;
    }

    const doCalculate = async () => {
      const requestId = ++fareCalcRequestIdRef.current;
      setCalculating(true);
      hasScrolledRef.current = false;
      setFeeError(null); // Limpia error antes de recalcular

      const availableOptions: Array<{ value: TransportOption['value']; label: string; icons: React.ElementType[] }> = [
        // DOCUMENTATION: Changed from 'Bici/Moto' with [Bike, Motorbike] to 'Bici' with [Bike] only
        { value: 'light', label: 'Bici', icons: [Bike] },
        // { value: 'auto', label: 'Auto', icons: [Car] },
        // { value: 'van', label: 'Camioneta', icons: [Truck] },
      ].filter((option) => (serviceType === 'persona' ? ['auto', 'van'].includes(option.value) : true));

      const nextOptions: TransportOption[] = [];

      for (const option of availableOptions) {
        const calcType = option.value === 'light' ? 'moto' : option.value;
        const driverCount = getDriverAvailability(option.value);
        const isDisabled = driverCount === 0;
        
        try {
          const optionResult = await calculateFee(validPickup, validDelivery, calcType, serviceType);
          if (requestId !== fareCalcRequestIdRef.current) return;
          if (optionResult) {
            nextOptions.push({
              value: option.value,
              label: option.label,
              icons: option.icons,
              fee: optionResult.fee,
              duration: optionResult.durationMin,
              disabled: isDisabled,
              disabledReason: isDisabled ? 'No hay repartidores de este tipo de vehículo disponible actualmente' : undefined,
            });
          }
        } catch (err) {
          // No setea error aquí, solo log
          console.error('[RequestRideSidebar] transport option calculation failed', { option: option.value, err });
        }
      }

      if (requestId !== fareCalcRequestIdRef.current) return;
      setTransportOptions(nextOptions);
      
      try {
        const result = await calculateFee(validPickup, validDelivery, transportType, serviceType);
        if (requestId !== fareCalcRequestIdRef.current) return;
        if (result) {
          setDistance(result.distanceKm);
          setFee(result.fee);
          setDuration(result.durationMin);
          setCurrentPriceBase(result.basePrice);
          setCurrentPricePerKm(result.pricePerKm);
          setFeeError(null); // Limpia error si hay resultado
        } else {
          setFeeError('No se pudo calcular la tarifa. Intenta de nuevo.');
        }
      } catch (err) {
        if (requestId !== fareCalcRequestIdRef.current) return;
        setFeeError('No se pudo calcular la tarifa. Intenta de nuevo.');
        console.error('Error calculating fee:', err);
      } finally {
        if (requestId !== fareCalcRequestIdRef.current) return;
        setCalculating(false);
      }
    };

    const timeoutId = setTimeout(doCalculate, 500);
    return () => clearTimeout(timeoutId);
  }, [
    selectedPickup?.coords.lat, 
    selectedPickup?.coords.lng, 
    selectedDelivery?.coords.lat, 
    selectedDelivery?.coords.lng, 
    normalizeLatLng,
    serviceType,
    toast,
    getDriverAvailability,
  ]);



  const handleSubmit = async () => {
    // Skip for tours - should use createTourReservation instead
    if (serviceType === 'tours') {
      return;
    }

    if (!session?.user?.id) {
      toast({
        title: 'Error',
        description: 'Debes iniciar sesión',
      });
      return;
    }

    if (!selectedPickup) {
      toast({
        title: 'Punto de retiro requerido',
        description: 'Por favor selecciona el punto de retiro',
      });
      return;
    }

    if (!selectedDelivery) {
      toast({
        title: 'Punto de destino requerido',
        description: 'Por favor selecciona el punto de destino',
      });
      return;
    }

    const normalizedPickupCoords = normalizeLatLng(selectedPickup.coords.lat, selectedPickup.coords.lng);
    const normalizedDeliveryCoords = normalizeLatLng(selectedDelivery.coords.lat, selectedDelivery.coords.lng);

    if (!normalizedPickupCoords || !normalizedDeliveryCoords) {
      toast({
        title: 'Coordenadas inválidas',
        description: 'No se pudo validar el punto de retiro o destino. Selecciónalos de nuevo.',
      });
      return;
    }

    const transportTypeMap: Record<string, string> = {
      'auto': 'car',
      'moto': 'motorcycle',
      'bici': 'bike',
      'bicicleta': 'bike',
      'bike': 'bike',
      'light': 'light',
      'van': 'van'
    };

    const serviceTypeMap: Record<string, string> = {
      'persona': 'driver',
      'paquete': 'courier'
    };

    const result = await requestRide(session.user.id, {
      transportType: transportTypeMap[transportType] || 'car',
      serviceType: serviceTypeMap[serviceType] || 'courier',
      pickupAddress: selectedPickup.displayName,
      deliveryAddress: selectedDelivery.displayName,
      pickupCoords: normalizedPickupCoords,
      deliveryCoords: normalizedDeliveryCoords,
      pickupSalutation: selectedPickup.salutation || null,
      pickupFirstName: selectedPickup.firstName || null,
      pickupLastName: selectedPickup.lastName || null,
      deliverySalutation: selectedDelivery.salutation || null,
      deliveryFirstName: selectedDelivery.firstName || null,
      deliveryLastName: selectedDelivery.lastName || null,
      deliveryFee: getEffectiveFeeForTransport(transportType) ?? selectedTransportFee ?? fee,
    });

    if (result.success) {
      toast({
        title: '✅ Viaje solicitado',
        description: `Se publicó tu viaje en el sistema. Distancia: ${result.distance?.toFixed(1)}km - Tarifa: ${formatCurrency(result.fee || 0)}`,
      });
      setTransportType('light');
      setSelectedPickup(null);
      setSelectedDelivery(null);
      setDistance(null);
      setFee(null);
      close();
    } else {
      toast({
        title: 'Error',
        description: result.message || 'No se pudo solicitar el viaje',
      });
    }
  };

  // Memoized callbacks for AddressPickerSidebar to prevent unnecessary re-renders
  const handleAddressPickerClose = useCallback(() => {
    setAddressPickerOpen(null);
    setInitialLocationForPicker(null);
    setInitialDisplayNameForPicker(null);
    setForceAlturaZeroForPicker(false);
  }, []);

  const handleSelectAddress = useCallback((address: AddressOption) => {
    if (addressPickerOpen === 'pickup') {
      setSelectedPickup(address);
      setAddressPickerOpen(null);
      setInitialLocationForPicker(null);
      setInitialDisplayNameForPicker(null);
    } else if (addressPickerOpen === 'delivery') {
      setSelectedDelivery(address);
      setAddressPickerOpen(null);
      setInitialLocationForPicker(null);
      setInitialDisplayNameForPicker(null);
    }
  }, [addressPickerOpen]);

  const handleUseCurrentLocationForPickup = useCallback(async () => {
    if (!navigator.geolocation) {
      toast({ title: 'GPS no disponible', description: 'Tu navegador no soporta geolocalización.' });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setInitialLocationForPicker({ lat: latitude, lng: longitude });
        setInitialDisplayNameForPicker(null); // Clear saved address when using GPS
        setForceAlturaZeroForPicker(false);
        setAddressPickerOpen('pickup');
      },
      (error) => {
        console.error('GPS error:', error);
        toast({ title: 'Error GPS', description: 'No fue posible obtener la ubicación.' });
      }
    );
  }, [toast]);

  const getCityFromDisplayName = useCallback((displayName: string): string => {
    const landmark = findCityLandmarkByDisplayName(displayName);
    if (landmark?.city) return landmark.city;

    const cityParts = displayName.split(',').map((part) => part.trim()).filter(Boolean);
    return cityParts.length > 1 ? cityParts[1] : displayName;
  }, []);

  const landmarkCardTitle = React.useMemo(() => {
    const displayName = relevantLocation?.displayName || profile?.relevant_location_display_name;
    if (!displayName) return 'Landmark de tu ciudad';

    const landmark = findCityLandmarkByDisplayName(displayName);
    if (landmark?.landmarkName) return landmark.landmarkName;

    return displayName.split(',')[0]?.trim() || displayName;
  }, [profile?.relevant_location_display_name, relevantLocation?.displayName]);

  const landmarkSelection = React.useMemo(() => {
    const displayName = relevantLocation?.displayName || profile?.relevant_location_display_name;
    const matchedLandmark = displayName ? findCityLandmarkByDisplayName(displayName) : null;

    if (matchedLandmark) {
      const normalized = normalizeLatLng(matchedLandmark.latitude, matchedLandmark.longitude);
      if (normalized) {
        return {
          idKey: matchedLandmark.displayName,
          displayName: matchedLandmark.displayName,
          coords: normalized,
        };
      }
    }

    const fallbackLat = relevantLocation?.latitude ?? profile?.relevant_latitude;
    const fallbackLng = relevantLocation?.longitude ?? profile?.relevant_longitude;
    const fallbackDisplayName = displayName;

    if (!fallbackDisplayName) return null;

    const fallbackCoords = normalizeLatLng(fallbackLat, fallbackLng);
    if (!fallbackCoords) return null;

    return {
      idKey: fallbackDisplayName,
      displayName: fallbackDisplayName,
      coords: fallbackCoords,
    };
  }, [normalizeLatLng, profile?.relevant_latitude, profile?.relevant_location_display_name, profile?.relevant_longitude, relevantLocation?.displayName, relevantLocation?.latitude, relevantLocation?.longitude]);

  const currentCityPrefill = React.useMemo(() => {
    const displayName = relevantLocation?.displayName || profile?.relevant_location_display_name;
    if (!displayName) return null;
    return getCityFromDisplayName(displayName);
  }, [getCityFromDisplayName, profile?.relevant_location_display_name, relevantLocation?.displayName]);

  const handleSelectAirport = useCallback((addressType: 'pickup' | 'delivery') => {
    // Extract city from displayName (e.g., "Plaza 9 de Julio, Salta, Argentina" -> "Salta")
    const displayName = relevantLocation?.displayName || profile?.relevant_location_display_name;
    if (!displayName) {
      toast({ title: 'Error', description: 'No se pudo determinar la ciudad actual.' });
      return;
    }

    const cityName = getCityFromDisplayName(displayName);

    const airport = getAirportByCity(cityName);
    if (airport) {
      const normalized = normalizeLatLng(airport.latitude, airport.longitude);
      if (!normalized) {
        toast({ title: 'Error', description: 'Coordenadas inválidas para el aeropuerto.' });
        return;
      }
      setInitialLocationForPicker(normalized);
      setInitialDisplayNameForPicker(airport.displayName);
      setForceAlturaZeroForPicker(true);
      setAddressPickerOpen(addressType);
    } else {
      toast({ title: 'Error', description: 'No se encontró el aeropuerto para esta ciudad.' });
    }
  }, [getCityFromDisplayName, normalizeLatLng, relevantLocation, profile?.relevant_location_display_name, toast]);

  const handleSelectBusTerminal = useCallback((addressType: 'pickup' | 'delivery') => {
    // Extract city from displayName
    const displayName = relevantLocation?.displayName || profile?.relevant_location_display_name;
    if (!displayName) {
      toast({ title: 'Error', description: 'No se pudo determinar la ciudad actual.' });
      return;
    }

    const cityName = getCityFromDisplayName(displayName);

    const terminal = getBusTerminalByCity(cityName);
    if (terminal) {
      const normalized = normalizeLatLng(terminal.latitude, terminal.longitude);
      if (!normalized) {
        toast({ title: 'Error', description: 'Coordenadas inválidas para la terminal de ómnibus.' });
        return;
      }
      setInitialLocationForPicker(normalized);
      setInitialDisplayNameForPicker(terminal.displayName);
      setForceAlturaZeroForPicker(true);
      setAddressPickerOpen(addressType);
    } else {
      toast({ title: 'Error', description: 'No se encontró la terminal de ómnibus para esta ciudad.' });
    }
  }, [getCityFromDisplayName, normalizeLatLng, relevantLocation, profile?.relevant_location_display_name, toast]);

  const handleSelectTrainStation = useCallback((addressType: 'pickup' | 'delivery') => {
    const displayName = relevantLocation?.displayName || profile?.relevant_location_display_name;
    if (!displayName) {
      toast({ title: 'Error', description: 'No se pudo determinar la ciudad actual.' });
      return;
    }

    const cityName = getCityFromDisplayName(displayName);

    const trainStation = getTrainStationByCity(cityName);
    if (trainStation) {
      const normalized = normalizeLatLng(trainStation.latitude, trainStation.longitude);
      if (!normalized) {
        toast({ title: 'Error', description: 'Coordenadas inválidas para la estación de trenes.' });
        return;
      }
      setInitialLocationForPicker(normalized);
      setInitialDisplayNameForPicker(trainStation.displayName);
      setForceAlturaZeroForPicker(true);
      setAddressPickerOpen(addressType);
    } else {
      toast({ title: 'Error', description: 'No se encontró la estación de trenes para esta ciudad.' });
    }
  }, [getCityFromDisplayName, normalizeLatLng, relevantLocation, profile?.relevant_location_display_name, toast]);

  const handleSelectLandmark = useCallback((addressType: 'pickup' | 'delivery') => {
    if (!landmarkSelection) return;
    
    setInitialLocationForPicker(landmarkSelection.coords);
    setInitialDisplayNameForPicker(landmarkSelection.displayName);
    setForceAlturaZeroForPicker(true);
    setAddressPickerOpen(addressType);
  }, [landmarkSelection]);

  return (
    <>
      <AnimatePresence>
        {(isOpen && !isClosing) && !addressPickerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-transparent z-[10000]"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleClose();
              }
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {(isOpen || isClosing) && (
        <motion.div
          ref={sidebarRef}
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ duration: 0.2 }}
          className="fixed right-0 w-full sm:w-[calc(100%-3rem)] lg:w-[400px] max-w-full bg-background shadow-2xl z-[10001] flex flex-col overflow-hidden"
          style={{
            top: '0',
            bottom: '0',
            height: isDesktop 
              ? '100vh' 
              : '100dvh',
            pointerEvents: (isOpen && !isClosing) ? 'auto' : 'none',
            maxWidth: 'calc(100vw - 0px)',
            width: isDesktop ? '400px' : 'calc(100vw - 0px)'
          }}
        >
          {/* Safe Area Spacer for Mobile - Only when showing main content */}
          {!showTourInfo && !checkoutOrder && !showPassengerSidebar && !isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}
          
          {/* Header - Only show when in main content */}
          {!showTourInfo && !checkoutOrder && !showPassengerSidebar && (
            <div className="flex items-center justify-between p-3 sm:p-4 border-b shrink-0 bg-primary text-primary-foreground min-h-[56px] sm:min-h-[64px]">
              <div className="flex items-center gap-2">
                {entryPoint === 'excursiones' ? (
                  <Binoculars className="w-5 h-5 text-white" />
                ) : (
                  // DOCUMENTATION: Changed to show only Bike icon
                  // Original showed: Bike, Motorbike, Car, Truck
                  <span className="inline-flex items-center gap-1 text-white" aria-hidden>
                    <Bike className="w-4 h-4" />
                    {/* HIDDEN - Other transport types:
                    <Motorbike className="w-4 h-4" />
                    <Car className="w-4 h-4" />
                    <Truck className="w-4 h-4" />
                    */}
                  </span>
                )}
                <h2 className="font-semibold text-lg text-white">
                  {entryPoint === 'excursiones' ? 'Actividades' : 'Envíos'}
                </h2>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleClose(); }}
                className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          )}

          {/* Content */}
          {showTourInfo ? (
            <TourInfoSidebar
              tour={showTourInfo}
              onBack={() => setShowTourInfo(null)}
            />
          ) : checkoutOrder ? (
            <TourCheckoutSummary
              tourTitle={selectedTours[0]?.title || ''}
              tourPrice={selectedTours[0]?.fixed_sale_price || 0}
              quantity={tourQuantity}
              tourDate={checkoutOrder.date}
              orderId={checkoutOrder.id}
              onClose={handleClose}
              onContinueShopping={() => {
                setCheckoutOrder(null);
                setSelectedTours([]);
                setTourQuantity(1);
                setSelectedCity(null);
                setSelectedPickup(null);
              }}
            />
          ) : showPassengerSidebar ? (
            <PassengerDetailsSidebar
              isOpen={showPassengerSidebar}
              onClose={() => setShowPassengerSidebar(false)}
              quantity={tourQuantity}
              selectedTours={selectedTours}
              initialPassengers={cartPassengerData?.passengers}
              initialTourDays={cartPassengerData?.tourDays}
              onProceedToCheckout={async (passengers, tourDays, totalPrice, pickup) => {
                if (!session?.user?.id || selectedTours.length === 0) return;

                setCalculating(true);
                try {
                  const storeId = selectedTours[0].store_id;

                  // If tours were not already in cart, clear old items and re-add
                  if (!cartPassengerData) {
                    await clearCartItemsForStore(session.user.id, storeId);
                    for (const tour of selectedTours) {
                      await addCartItem(session.user.id, tour.id, tourQuantity);
                    }
                  }

                  // Update cart notes with confirmed passenger/itinerary data (for compatibility)
                  const metaDocs = {
                    type: 'tour_cart_passenger_details',
                    passengers,
                    itinerary: tourDays.map(d => ({
                      date: d.date,
                      tourId: d.tour.id,
                      tourTitle: d.tour.title,
                      destination: d.destination?.displayName,
                    })),
                    passengerCount: passengers.length,
                    totalPrice,
                    updatedAt: new Date().toISOString(),
                    pickup: pickup ? {
                      displayName: pickup.displayName,
                      coords: pickup.coords,
                      floor: pickup.floor,
                      apartment: pickup.apartment,
                    } : undefined,
                  };

                  // Only save passenger details to cart_items.details (primary storage)
                  const itemDetails = {
                    passengers: passengers.map(p => ({
                      id: p.id,
                      name: p.name,
                      documentNumber: p.documentNumber,
                      nationality: p.nationality,
                    })),
                    tourDays: tourDays.map(d => ({
                      date: d.date,
                      tourId: d.tour.id,
                      tourTitle: d.tour.title,
                      destination: d.destination?.displayName,
                    })),
                    pickupLocation: pickup ? {
                      displayName: pickup.displayName,
                      lat: pickup.coords.lat,
                      lng: pickup.coords.lng,
                      floor: pickup.floor,
                      apartment: pickup.apartment,
                    } : undefined,
                    totalPrice,
                    passengerCount: passengers.length,
                    updatedAt: new Date().toISOString(),
                  };

                  console.log('💾 [RequestRideSidebar] Saving passenger details to cart_items:', itemDetails);
                  await updateCartItemsDetailsForStore(session.user.id, storeId, itemDetails);
                  console.log('✅ [RequestRideSidebar] Passenger details saved successfully');

                  // Dispatch cart update event and wait a bit for refresh
                  window.dispatchEvent(new CustomEvent('cart-updated'));
                  
                  // Add small delay to ensure cart refresh completes
                  await new Promise(resolve => setTimeout(resolve, 200));

                  setShowPassengerSidebar(false);
                  handleClose();
                  openCheckoutSidebar();
                } catch (error) {
                  console.error('Error synchronizing cart:', error);
                  toast({
                    title: 'Error al preparar el canasto',
                    description: 'No pudimos guardar tu selección. Por favor reintenta.',
                    variant: 'destructive'
                  });
                } finally {
                  setCalculating(false);
                }
              }}
              loading={calculating}
            />
          ) : tourPaymentData ? (
            <div className="flex flex-col h-full">
              <div className="p-4 border-b bg-primary text-white flex items-center gap-2">
                <button onClick={() => setTourPaymentData(null)}><ChevronLeft className="w-5 h-5" /></button>
                <h2 className="font-semibold">Confirmar Pago</h2>
              </div>
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  <div className="bg-muted/30 p-4 rounded-xl border space-y-3">
                    <h3 className="font-bold text-sm uppercase text-primary">Resumen de Reserva</h3>
                    {tourPaymentData.tourDays.map((day: any, i: number) => (
                      <div key={i} className="text-sm border-b pb-2 last:border-0">
                        <div className="font-bold">{day.tour.title}</div>
                        <div className="text-xs text-muted-foreground">{day.date}</div>
                        <div className="text-xs text-primary font-medium">Destino: {day.destination?.displayName}</div>
                      </div>
                    ))}
                    <div className="pt-2 flex justify-between items-center border-t font-bold">
                      <span>Total</span>
                      <span className="text-lg">{formatCurrency(tourPaymentData.totalPrice)}</span>
                    </div>
                  </div>
                  
                  <PaymentSidebar
                    amount={tourPaymentData.totalPrice}
                    onPaymentSuccess={async () => {
                      if (!session?.user?.id || !selectedPickup) return;
                      
                      const results = await Promise.all(tourPaymentData.tourDays.map(day => 
                        createTourReservation(session.user.id, {
                          tourProductId: day.tour.id,
                          buyerUserId: session.user.id,
                          quantityPeople: tourQuantity,
                          passengers: tourPaymentData.passengers.map(p => ({
                            id: p.id,
                            name: p.name,
                            dniNumber: p.documentNumber
                          })),
                          tourDate: day.date,
                          pickupAddress: selectedPickup.displayName,
                          pricePerPerson: day.tour.fixed_sale_price
                        })
                      ));

                      const firstSuccess = results.find(r => r.success);
                      if (firstSuccess) {
                        setCheckoutOrder({
                          id: firstSuccess.orderId!,
                          date: tourPaymentData.tourDays[0].date,
                          total: tourPaymentData.totalPrice
                        });
                        setTourPaymentData(null);
                      } else {
                        toast({ title: 'Error', description: 'No se pudo procesar la reserva' });
                      }
                    }}
                    orderId="multiple-tours"
                  />
                </div>
              </ScrollArea>
            </div>
          ) : (
            <>
          <ScrollArea ref={scrollAreaRef} className="flex-1 overflow-hidden">
            <div className="p-3 sm:p-4 space-y-4 w-full box-border">
              {serviceType !== 'tours' && (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold block">Tus pedidos con envío Minibid</Label>
                  {session?.user?.id ? (
                    // For logged-in users: show their delivery orders
                    loadingDeliveryOrders ? (
                      <div className="space-y-2">
                        {[1, 2].map((idx) => (
                          <div key={idx} className="relative overflow-hidden rounded-2xl border bg-card p-3.5">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <Skeleton className="h-11 w-11 rounded-xl shrink-0" />
                                <div className="min-w-0 flex-1 space-y-2">
                                  <Skeleton className="h-4 w-36" />
                                  <Skeleton className="h-3 w-24" />
                                  <Skeleton className="h-3 w-32" />
                                </div>
                              </div>
                              <div className="space-y-2 shrink-0">
                                <Skeleton className="h-3 w-12 ml-auto" />
                                <Skeleton className="h-5 w-20 ml-auto" />
                              </div>
                            </div>
                            <div className="mt-3 space-y-2 rounded-lg border border-border/60 bg-background/70 p-2">
                              <Skeleton className="h-3 w-full" />
                              <Skeleton className="h-1.5 w-full rounded-full" />
                              <Skeleton className="h-3 w-full" />
                              <Skeleton className="h-1.5 w-full rounded-full" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : deliveryOrders.length > 0 ? (
                      <div className="space-y-2">
                        {deliveryOrders.slice(0, visibleDeliveryOrdersCount).map(renderDeliveryOrderCard)}
                        {deliveryOrders.length > visibleDeliveryOrdersCount && (
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            onClick={() => setVisibleDeliveryOrdersCount((prev) => prev + 3)}
                          >
                            Cargar más ({Math.max(0, deliveryOrders.length - visibleDeliveryOrdersCount)})
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed p-3 sm:p-4 text-sm text-muted-foreground text-center space-y-2 w-full">
                        <div className="flex justify-center">
                          <Package className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground/60" />
                        </div>
                        <div className="font-medium text-sm sm:text-base">Aún no tienes envíos</div>
                        <div className="text-xs leading-relaxed">Solicita tu primer viaje completando los datos de abajo</div>
                      </div>
                    )
                  ) : (
                    // For non-logged-in users: show empty state with call to action
                    <div className="rounded-lg border border-dashed p-3 sm:p-4 text-sm text-muted-foreground text-center space-y-2 w-full">
                      <div className="flex justify-center">
                        <Package className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground/60" />
                      </div>
                      <div className="font-medium text-sm sm:text-base">Aún no tienes envíos</div>
                    </div>
                  )}
                </div>
              )}

              {/* DOCUMENTATION: Added semiheader for bike-only delivery */}
              <div>
                <Label className="text-sm font-semibold block">Solicitar cadete privado</Label>
              </div>

              {/* Punto de Retiro */}
              <div>
                <Label className="mb-2 flex items-center gap-2 text-sm font-semibold">
                  <PlaneTakeoff className="h-4 w-4 animate-[bounce_2.2s_ease-in-out_infinite] text-primary" />
                  <span>Punto de Retiro</span>
                </Label>
                {selectedPickup ? (
                  <div className="flex items-center justify-between border rounded p-3 min-h-[56px] bg-muted">
                    <div className="flex-1 min-w-0">
                      {(selectedPickup.salutation || selectedPickup.firstName || selectedPickup.lastName) && (
                        <div className="text-xs text-muted-foreground mb-1">
                          {[selectedPickup.salutation, selectedPickup.firstName, selectedPickup.lastName].filter(Boolean).join(' ')}
                        </div>
                      )}
                      <div className="font-bold text-sm">{formatAddressForDisplay(selectedPickup.displayName)}</div>
                      {(selectedPickup.floor || selectedPickup.apartment) && (
                        <div className="text-xs text-muted-foreground">
                          {[selectedPickup.floor ? `Piso ${selectedPickup.floor}` : null, selectedPickup.apartment ? `Depto ${selectedPickup.apartment}` : null].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPickup(null);
                      }}
                    >
                      Cambiar
                    </Button>
                  </div>
                ) : addresses.length === 0 ? (
                  <div className="space-y-3">
                    {/* Search input that opens AddressPickerSidebar */}
                    <button
                      onClick={() => {
                        setForceAlturaZeroForPicker(false);
                        setInitialLocationForPicker(null);
                        setInitialDisplayNameForPicker(currentCityPrefill);
                        setAddressPickerOpen('pickup');
                      }}
                      className="w-full flex items-center gap-3 border-2 border-primary rounded-lg p-3 min-h-[56px] bg-primary/5 hover:bg-primary/10 transition-colors text-left font-semibold"
                    >
                      <svg className="w-5 h-5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span className="text-sm">Buscar dirección...</span>
                    </button>

                    {/* Tu ubicación actual button */}
                    <button
                      type="button"
                      onClick={handleUseCurrentLocationForPickup}
                      className="flex items-center border rounded p-3 min-h-[56px] w-full bg-background hover:bg-muted transition-colors cursor-pointer gap-2"
                    >
                      <LocateFixed className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="font-bold text-sm">Tu ubicación actual</span>
                    </button>

                    {/* Predefined locations for pickup */}
                    <div className="space-y-2 mt-3 pt-3 border-t">
                      <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        onClick={() => handleSelectAirport('pickup')}

                        className="w-full flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors cursor-pointer"
                      >
                        <Plane className="h-5 w-5 text-primary flex-shrink-0" />
                        <div className="text-left flex-1">
                          <div className="font-bold text-sm">Aeropuerto</div>
                        </div>
                      </motion.button>
                      <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                        onClick={() => handleSelectBusTerminal('pickup')}

                        className="w-full flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors cursor-pointer"
                      >
                        <Bus className="h-5 w-5 text-primary flex-shrink-0" />
                        <div className="text-left flex-1">
                          <div className="font-bold text-sm">Terminal de Ómnibus</div>
                        </div>
                      </motion.button>
                      <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        onClick={() => handleSelectTrainStation('pickup')}
                        className="w-full flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors cursor-pointer"
                      >
                        <TramFront className="h-5 w-5 text-primary flex-shrink-0" />
                        <div className="text-left flex-1">
                          <div className="font-bold text-sm">Estación de Trenes</div>
                        </div>
                      </motion.button>
                      {landmarkSelection && (
                        <motion.button
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.15 }}
                          onClick={() => handleSelectLandmark('pickup')}
                          className="w-full flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors cursor-pointer"
                        >
                          <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
                          <div className="text-left flex-1">
                            <div className="font-bold text-sm">{landmarkCardTitle}</div>
                          </div>
                        </motion.button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Search input that opens AddressPickerSidebar */}
                    <button
                      onClick={() => {
                        setForceAlturaZeroForPicker(false);
                        setInitialLocationForPicker(null);
                        setInitialDisplayNameForPicker(currentCityPrefill);
                        setAddressPickerOpen('pickup');
                      }}
                      className="w-full flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors text-left"
                    >
                      <svg className="w-5 h-5 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span className="text-sm text-muted-foreground">Buscar dirección...</span>
                    </button>

                    {/* Tu ubicación actual button */}
                    <button
                      type="button"
                      onClick={handleUseCurrentLocationForPickup}
                      className="flex items-center border rounded p-3 min-h-[56px] w-full bg-background hover:bg-muted transition-colors cursor-pointer gap-2"
                    >
                      <LocateFixed className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="font-bold text-sm">Tu ubicación actual</span>
                    </button>

                    {/* Saved addresses */}
                    {addresses.length > 0 && (
                      <div className="space-y-2">
                        <AnimatePresence>
                          {addresses.map((addr) => (
                            <motion.div
                              key={addr.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.95 }}
                              onClick={() => {
                                setInitialLocationForPicker({ lat: addr.location.latitude, lng: addr.location.longitude });
                                setInitialDisplayNameForPicker(addr.location.displayName);
                                setForceAlturaZeroForPicker(false);
                                setAddressPickerOpen('pickup');
                              }}
                              className="flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors cursor-pointer"
                            >
                              <div className="flex-1">
                                <div className="font-bold text-sm">{formatAddressForDisplay(addr.label || addr.location.displayName)}</div>
                                {(addr.floor || addr.apartment) && (
                                  <div className="text-xs text-muted-foreground">
                                    {[addr.floor ? `Piso ${addr.floor}` : null, addr.apartment ? `Depto ${addr.apartment}` : null].filter(Boolean).join(' · ')}
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                      </div>
                    )}

                    {/* Predefined locations for pickup */}
                    <div className="space-y-2 mt-3 pt-3 border-t">
                      <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        onClick={() => handleSelectAirport('pickup')}

                        className="w-full flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors cursor-pointer"
                      >
                        <Plane className="h-5 w-5 text-primary flex-shrink-0" />
                        <div className="text-left flex-1">
                          <div className="font-bold text-sm">Aeropuerto</div>
                        </div>
                      </motion.button>
                      <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                        onClick={() => handleSelectBusTerminal('pickup')}

                        className="w-full flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors cursor-pointer"
                      >
                        <Bus className="h-5 w-5 text-primary flex-shrink-0" />
                        <div className="text-left flex-1">
                          <div className="font-bold text-sm">Terminal de Ómnibus</div>
                        </div>
                      </motion.button>
                      <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        onClick={() => handleSelectTrainStation('pickup')}
                        className="w-full flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors cursor-pointer"
                      >
                        <TramFront className="h-5 w-5 text-primary flex-shrink-0" />
                        <div className="text-left flex-1">
                          <div className="font-bold text-sm">Estación de Trenes</div>
                        </div>
                      </motion.button>
                      {landmarkSelection && (
                        <motion.button
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.15 }}
                          onClick={() => handleSelectLandmark('pickup')}
                          className="w-full flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors cursor-pointer"
                        >
                          <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
                          <div className="text-left flex-1">
                            <div className="font-bold text-sm">{landmarkCardTitle}</div>
                          </div>
                        </motion.button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Tours City Selection - COMMENTED TEMPORARILY */}
              {/* {serviceType === 'tours' && (
                <div className="space-y-4">
                  <TourCitySelector
                    selectedCity={selectedCity}
                    onCitySelect={setSelectedCity}
                  />
                </div>
              )} */}

              {/* Tours Selection (when tours service selected) */}
              {selectedPickup && serviceType === 'tours' && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold mb-2 block">Excursiones Disponibles</Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      Agrégalas directo al canasto. Luego completas pasajeros y fechas desde tus pedidos.
                    </p>
                    <ToursCardList
                      userLocation={{ latitude: selectedPickup.coords.lat, longitude: selectedPickup.coords.lng }}
                      radius={50}
                      selectedTourIds={selectedTours.map(t => t.id)}
                      onSelectTour={toggleTourSelection}
                      onViewTourInfo={(tour) => setShowTourInfo(tour)}
                    />
                  </div>
                </div>
              )}

              {/* Punto de Destino (for non-tours) */}
              {selectedPickup && serviceType !== 'tours' && (
                <div>
                  <Label className="mb-2 flex items-center gap-2 text-sm font-semibold">
                    <PlaneLanding className="h-4 w-4 animate-[bounce_2.2s_ease-in-out_infinite] text-primary [animation-delay:120ms]" />
                    <span>Punto de Destino</span>
                  </Label>
                  {selectedDelivery ? (
                    <div className="flex items-center justify-between border rounded p-3 min-h-[56px] bg-muted">
                      <div className="flex-1">
                        {(selectedDelivery.salutation || selectedDelivery.firstName || selectedDelivery.lastName) && (
                          <div className="text-xs text-muted-foreground mb-1">
                            {[selectedDelivery.salutation, selectedDelivery.firstName, selectedDelivery.lastName].filter(Boolean).join(' ')}
                          </div>
                        )}
                        <div className="font-bold text-sm">{formatAddressForDisplay(selectedDelivery.displayName)}</div>
                        {(selectedDelivery.floor || selectedDelivery.apartment) && (
                          <div className="text-xs text-muted-foreground">
                            {[selectedDelivery.floor ? `Piso ${selectedDelivery.floor}` : null, selectedDelivery.apartment ? `Depto ${selectedDelivery.apartment}` : null].filter(Boolean).join(' · ')}
                          </div>
                        )}
                        {selectedDelivery.distance !== undefined && (
                          <div className="text-xs text-primary font-medium mt-1">
                            ~{selectedDelivery.distance.toFixed(1)}km
                          </div>
                        )}
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setSelectedDelivery(null)}>
                        Cambiar
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Search input that opens AddressPickerSidebar */}
                      <button
                        onClick={() => {
                          setForceAlturaZeroForPicker(false);
                          setInitialLocationForPicker(null);
                          setInitialDisplayNameForPicker(currentCityPrefill);
                          setAddressPickerOpen('delivery');
                        }}
                        className="w-full flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors text-left"
                      >
                        <svg className="w-5 h-5 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <span className="text-sm text-muted-foreground">Buscar dirección...</span>
                      </button>

                      {/* Tu ubicación actual button */}
                      <button
                        type="button"
                        onClick={() => {
                          navigator.geolocation.getCurrentPosition(
                            (position) => {
                              const { latitude, longitude } = position.coords;
                              setInitialLocationForPicker({ lat: latitude, lng: longitude });
                              setInitialDisplayNameForPicker(null);
                              setForceAlturaZeroForPicker(false);
                              setAddressPickerOpen('delivery');
                            },
                            (error) => {
                              toast({ title: 'Error GPS', description: 'No fue posible obtener la ubicación.' });
                            }
                          );
                        }}
                        className="flex items-center border rounded p-3 min-h-[56px] w-full bg-background hover:bg-muted transition-colors cursor-pointer gap-2"
                      >
                        <LocateFixed className="h-5 w-5 text-primary flex-shrink-0" />
                        <span className="font-bold text-sm">Tu ubicación actual</span>
                      </button>

                      {/* Saved addresses */}
                      {addresses.length > 0 && (
                        <div className="space-y-2">
                          <AnimatePresence>
                            {addresses.map((addr) => (
                              <motion.div
                                key={addr.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                onClick={() => {
                                  setInitialLocationForPicker({ lat: addr.location.latitude, lng: addr.location.longitude });
                                  setInitialDisplayNameForPicker(addr.location.displayName);
                                  setAddressPickerOpen('delivery');
                                }}
                                className="flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors cursor-pointer"
                              >
                                <div className="flex-1">
                                  <div className="font-bold text-sm">{formatAddressForDisplay(addr.label || addr.location.displayName)}</div>
                                  {(addr.floor || addr.apartment) && (
                                    <div className="text-xs text-muted-foreground">
                                      {[addr.floor ? `Piso ${addr.floor}` : null, addr.apartment ? `Depto ${addr.apartment}` : null].filter(Boolean).join(' · ')}
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            ))}
                          </AnimatePresence>
                        </div>
                      )}

                      {/* Predefined locations for delivery */}
                      <div className="space-y-2 mt-3 pt-3 border-t">
                        <motion.button
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          onClick={() => handleSelectAirport('delivery')}
                          className="w-full flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors cursor-pointer"
                        >
                          <Plane className="h-5 w-5 text-primary flex-shrink-0" />
                          <div className="text-left flex-1">
                            <div className="font-bold text-sm">Aeropuerto</div>
                          </div>
                        </motion.button>
                        <motion.button
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.05 }}
                          onClick={() => handleSelectBusTerminal('delivery')}
                          className="w-full flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors cursor-pointer"
                        >
                          <Bus className="h-5 w-5 text-primary flex-shrink-0" />
                          <div className="text-left flex-1">
                            <div className="font-bold text-sm">Terminal de Ómnibus</div>
                          </div>
                        </motion.button>
                        <motion.button
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 }}
                          onClick={() => handleSelectTrainStation('delivery')}
                          className="w-full flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors cursor-pointer"
                        >
                          <TramFront className="h-5 w-5 text-primary flex-shrink-0" />
                          <div className="text-left flex-1">
                            <div className="font-bold text-sm">Estación de Trenes</div>
                          </div>
                        </motion.button>
                        {landmarkSelection && (
                          <motion.button
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15 }}
                            onClick={() => handleSelectLandmark('delivery')}
                            className="w-full flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors cursor-pointer"
                          >
                            <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
                            <div className="text-left flex-1">
                              <div className="font-bold text-sm">{landmarkCardTitle}</div>
                            </div>
                          </motion.button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Aviso de error de tarifa */}
              {feeError && (
                <div className="bg-red-100 text-red-700 rounded p-3 text-sm mb-2">
                  {feeError}
                </div>
              )}

              {selectedPickup && selectedDelivery && serviceType !== 'tours' && (
                <div ref={calculationSkeletonRef} className="mt-4 space-y-4">
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-semibold mb-2 block">
                        {calculating ? 'Calculando opciones...' : 'Repartidor disponible'}
                      </Label>
                    </div>

                    {calculating ? (
                      <div className="space-y-2">
                        {[1, 2, 3].map((idx) => (
                          <div key={idx} className="w-full p-4 border-2 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Skeleton className="h-5 w-5 rounded-full" />
                              <div className="flex shrink-0 gap-1">
                                {Array.from({ length: idx === 1 ? 2 : 1 }).map((_, iconIdx) => (
                                  <Skeleton key={`${idx}-${iconIdx}`} className="h-5 w-5 rounded" />
                                ))}
                              </div>
                              <div className="flex-1 flex justify-between items-center gap-2">
                                <Skeleton className="h-4 w-24" />
                                <div className="flex items-center gap-2">
                                  <Skeleton className="h-3 w-10" />
                                  <Skeleton className="h-4 w-16" />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : distance !== null && fee !== null ? (
                      <div className="space-y-2">
                        {transportOptions
                          .filter((option) => {
                            // DOCUMENTATION: Only show 'light' (Bike) option
                            // Original showed: light, auto, van
                            // Hidden transport options: auto, van (filter them out)
                            return option.value === 'light';
                          })
                          .map(({ value, label, icons, fee: optionFee, duration: optionDuration, disabled, disabledReason }) => {
                            const displayFee = optionFee ?? null;
                            const displayDuration = optionDuration ?? duration;

                            return (
                            <div key={value} className="relative">
                              <button
                                key={value}
                                onClick={() => {
                                  if (!disabled) {
                                    setTransportType(value);
                                    setSelectedTransportFee(getEffectiveFeeForTransport(value));
                                  }
                                }}
                                disabled={disabled}
                                className={cn(
                                  "w-full p-4 border-2 rounded-lg transition-all",
                                  disabled ? "opacity-50 cursor-not-allowed border-muted/30" : 
                                  transportType === value
                                    ? "border-primary bg-primary/5"
                                    : "border-border hover:border-primary/50"
                                )}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={cn(
                                    "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                                    disabled ? "border-muted/30" :
                                    transportType === value ? "border-primary bg-primary" : "border-muted-foreground/30"
                                  )}>
                                    {!disabled && transportType === value && <div className="w-2 h-2 bg-white rounded-full" />}
                                  </div>
                                  <div className="flex-shrink-0 flex gap-1">
                                    {icons.map((IconComponent, idx) => (
                                      <IconComponent key={idx} className={cn("w-5 h-5", disabled ? "text-muted/30" : transportType === value ? "text-primary" : "text-muted-foreground")} />
                                    ))}
                                  </div>
                                  <div className="flex-1 flex justify-between items-center gap-2">
                                    <div className="font-semibold text-sm">{label}</div>
                                    <div className="flex items-center gap-2">
                                      {displayDuration !== null && (
                                        <span className="text-xs font-medium text-muted-foreground">
                                          {Math.round((value === 'light' ? displayDuration * 0.8 : displayDuration) * 1.5)} min
                                        </span>
                                      )}
                                      <div className={cn("text-sm font-bold", disabled ? "text-muted/50" : "text-primary")}>
                                        {displayFee !== null ? formatCurrency(value === 'light' ? displayFee * 0.7 : displayFee) : '...'}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </button>
                              {disabled && disabledReason && (
                                <div className="mt-2 px-2">
                                  <div className="inline-block px-2.5 py-1 bg-muted/40 border border-muted/50 rounded-full">
                                    <span className="text-xs font-medium text-muted-foreground">{disabledReason}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                            );
                          })}
                      </div>
                    ) : null}
                  </div>

                  {calculating ? (
                    <div className="bg-primary/5 border border-border rounded-lg p-4 space-y-3">
                      <div className="flex justify-between items-center">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-7 w-28" />
                      </div>
                      <div className="flex justify-between items-center border-t border-border pt-3">
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                    </div>
                  ) : distance !== null && fee !== null ? (
                    <div className="bg-primary/5 border border-border rounded-lg p-4 space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-foreground/70">Precio Final</span>
                          <span className="text-xl font-black text-primary">
                            {(() => {
                              // Usar selectedTransportFee si existe (es el que se va a guardar)
                              // Si no, calcular desde transportOptions
                              const displayPrice = getEffectiveFeeForTransport(transportType) ?? selectedTransportFee ?? fee;
                              return displayPrice !== null ? formatCurrency(displayPrice) : '...';
                            })()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center border-t border-border pt-3">
                          <span className="text-sm font-medium text-foreground/70">Distancia y Tiempo</span>
                          <span className="text-sm font-bold text-primary/80">
                            {distance.toFixed(1)} km · {Math.round((transportType === 'light' ? duration! * 0.8 : duration!) * 1.5)} min
                          </span>
                        </div>
                        <div className="pt-2 border-t border-border">
                          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                            Al solicitar este viaje aceptas nuestros <span className="font-semibold">Términos y Condiciones</span> y <span className="font-semibold">Política de Privacidad</span>.
                          </p>
                        </div>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Error display */}
              <AnimatePresence>
                {error && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 overflow-hidden"
                  >
                    <p className="text-sm text-destructive font-medium">{error}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </ScrollArea>

          {/* Footer - Show conditional buttons based on service type */}
          {serviceType === 'tours' ? (
            selectedTours.length > 0 && (
              <div className="border-t p-3 sm:p-4 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] sm:pb-[calc(1rem+env(safe-area-inset-bottom,0px))] bg-background/80 backdrop-blur-md shrink-0">
                <div className="space-y-3">
                  <div className="flex justify-between items-center px-1 gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs sm:text-sm font-bold text-primary whitespace-nowrap">
                        {selectedTours.length} {selectedTours.length === 1 ? 'Excursión' : 'Excursiones'}
                      </span>
                      <div className="flex -space-x-1.5 overflow-hidden shrink-0">
                        {selectedTours.slice(0, 3).map((t) => (
                          <div key={t.id} className="w-5 h-5 rounded-full border-2 border-background overflow-hidden bg-muted shrink-0">
                            <img src={t.main_image_url || '/placeholder-tour.jpg'} alt={t.title} className="w-full h-full object-cover" />
                          </div>
                        ))}
                        {selectedTours.length > 3 && (
                          <div className="w-5 h-5 rounded-full border-2 border-background overflow-hidden bg-primary/20 text-primary text-[10px] flex items-center justify-center font-bold">
                            +{selectedTours.length - 3}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); void handleClearSelectedTours(); }} 
                      className="text-xs h-7 text-muted-foreground hover:text-destructive px-2 shrink-0"
                    >
                      Limpiar
                    </Button>
                  </div>
                  <Button
                    onClick={handleOpenToursCheckout}
                    className="w-full font-bold h-11 sm:h-12 text-sm sm:text-base shadow-md shadow-primary/20"
                  >
                    Ir al canasto con {selectedTours.length} {selectedTours.length === 1 ? 'Excursión' : 'Excursiones'}
                  </Button>
                </div>
              </div>
            )
          ) : (
            // Regular ride footer
            selectedPickup && selectedDelivery && (
              <div className="border-t pt-3 sm:pt-4 px-3 sm:px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] sm:pb-[calc(1rem+env(safe-area-inset-bottom,0px))] space-y-2 bg-muted/20 shrink-0">
                <Button
                  onClick={handleSubmit}
                  disabled={loading || calculating}
                  size="lg"
                  className="w-full active:scale-95 h-11 sm:h-12 text-sm sm:text-base"
                >
                  {loading ? (
                    'Solicitando...'
                  ) : (
                    <motion.span
                      className="inline-flex items-center justify-center gap-2 leading-none"
                      animate={{ opacity: [1, 0.62, 1] }}
                      transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      {!calculating && (
                        <span className="inline-flex items-center gap-1.5">
                          {selectedTransportIcons.map((IconComponent, idx) => (
                            <IconComponent key={idx} className="h-4 w-4 shrink-0" />
                          ))}
                        </span>
                      )}
                      <span>{calculating ? 'Calculando...' : `Solicitar servicio de ${selectedTransportLabel}`}</span>
                    </motion.span>
                  )}
                </Button>
              </div>
            )
          )}
            </>
          )}
        </motion.div>
        )}
      </AnimatePresence>

      {/* Address Picker Sidebar */}
      <AddressPickerSidebar
        ref={addressPickerSidebarRef}
        isOpen={addressPickerOpen !== null}
        initialDisplayName={initialDisplayNameForPicker}
        addressType={addressPickerOpen}
        onClose={handleAddressPickerClose}
        onSelectAddress={handleSelectAddress}
        userAddresses={addresses}
        sessionUserId={session?.user?.id}
        initialLocation={initialLocationForPicker}
        forceAlturaZero={forceAlturaZeroForPicker}
        overlayZIndex={10002}
        panelZIndex={10003}
        fullViewport={true}
      />

      {/* Tour Payment Sidebar */}
      {tourPaymentData && (
        <PaymentSidebar
          isOpen={true}
          onClose={() => {
            setTourPaymentData(null);
          }}
          onConfirm={async (buyerCbu, buyerBankName, buyerAccountHolder, orderNotes, paymentMethod) => {
            if (!session?.user?.id || !selectedTours[0] || !tourPaymentData) return;

            const selectedTour = selectedTours[0];
            const { passengers, tourDate } = tourPaymentData;
            setCalculating(true);
            try {
              const result = await createTourReservation(session.user.id, {
                tourProductId: selectedTour.id,
                buyerUserId: session.user.id,
                quantityPeople: tourQuantity,
                passengers,
                tourDate,
                pickupAddress: selectedPickup?.displayName || '',
                pricePerPerson: selectedTour.fixed_sale_price || 0,
                notes: orderNotes
              });

              if (result.success && result.orderId) {
                setTourPaymentData(null);
                setShowPassengerSidebar(false);
                setCheckoutOrder({
                  id: result.orderId,
                  date: tourDate,
                  total: tourPaymentData.totalPrice,
                });
                toast({
                  title: '✅ Reserva confirmada',
                  description: `Orden #${result.orderId.slice(0, 8)} creada exitosamente`,
                });
              } else {
                toast({
                  title: 'Error',
                  description: result.message || 'No se pudo crear la reserva',
                  variant: 'destructive',
                });
              }
            } catch (error) {
              console.error('Error creating tour reservation:', error);
              toast({
                title: 'Error',
                description: 'Ocurrió un error inesperado',
                variant: 'destructive',
              });
            } finally {
              setCalculating(false);
            }
          }}
          storeId={selectedTours[0]?.store_id || ''}
          storeName={selectedTours[0]?.store_name || 'Tour Provider'}
          total={tourPaymentData.totalPrice}
          buyerProfile={profile ? {
            bank_cbu: profile.bank_cbu,
            bank_name: profile.bank_name,
            bank_account_holder: profile.bank_account_holder,
            bank_accounts: profile.bank_accounts,
          } : null}
          userId={session?.user?.id || ''}
          isEmbedded={true}
          isDigital={selectedTours[0]?.is_digital}
          userEmail={profile?.email}
          userPhoneNumber={profile?.phone_number}
        />
      )}
    </>
  );
}